"use client";

import { useRef, useState, useTransition } from "react";
import Link from "next/link";
import {
  CAPTCHA_REQUIRED_MESSAGE,
  getCaptchaErrorMessage,
} from "@/lib/turnstile-client";
import TurnstileWidget from "./TurnstileWidget";
import { isTurnstileWidgetEnabled } from "@/lib/turnstile-widget";
import { toLocalizedPath, type Locale } from "@/lib/i18n";

export default function PasswordResetRequestForm({ locale = "ko" }: { locale?: Locale }) {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const turnstileTokenRef = useRef<string | null>(null);
  const [turnstileResetSignal, setTurnstileResetSignal] = useState(0);
  const [pending, startTransition] = useTransition();
  const copy = locale === "en"
    ? { required: "Enter your email.", limited: "Too many attempts. Please try again later.", failed: "The request failed.", network: "A network error occurred.", done: "Request sent", doneBody: "If this address belongs to an account, we’ll send a password reset email. Check your spam folder if you don’t see it.", login: "Go to log in", title: "Reset your password", intro: "Enter the email you used to sign up. We’ll send a reset link if it belongs to an account.", email: "Email", pending: "Sending…", submit: "Send reset link", back: "Back to", backLabel: "log in" }
    : { required: "이메일을 입력해 주세요.", limited: "시도가 너무 많아요. 잠시 후 다시 시도해 주세요.", failed: "요청에 실패했어요.", network: "네트워크 오류가 발생했어요.", done: "요청 완료", doneBody: "입력하신 주소가 가입된 계정이라면 비밀번호 재설정 메일이 발송돼요. 메일이 도착하지 않는다면 스팸함도 확인해 주세요.", login: "로그인으로 이동", title: "비밀번호 재설정", intro: "가입 시 사용한 이메일을 입력해 주세요. 가입된 주소라면 재설정 링크가 발송됩니다.", email: "이메일", pending: "요청 중…", submit: "재설정 링크 발송", back: "로그인으로", backLabel: "돌아가기" };

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email) {
      setError(copy.required);
      return;
    }
    const turnstileToken = turnstileTokenRef.current;
    if (isTurnstileWidgetEnabled() && !turnstileToken) {
      setError(locale === "en" ? "Please complete the verification." : CAPTCHA_REQUIRED_MESSAGE);
      return;
    }
    setError(null);
    startTransition(async () => {
      try {
        const res = await fetch("/api/auth/password-reset/request", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, turnstileToken, locale }),
        });
        const captchaError = await getCaptchaErrorMessage(res, locale);
        if (captchaError) {
          setError(captchaError);
          return;
        }
        if (res.status === 429) {
          setError(copy.limited);
          return;
        }
        if (!res.ok) {
          setError(copy.failed);
          return;
        }
        setSubmitted(true);
      } catch {
        setError(copy.network);
      } finally {
        turnstileTokenRef.current = null;
        setTurnstileResetSignal((value) => value + 1);
      }
    });
  }

  if (submitted) {
    return (
      <section className="flex w-full max-w-sm flex-col gap-4 rounded-3xl bg-white/80 p-6 shadow-pop">
        <h1 className="font-display text-xl text-slate-900">{copy.done}</h1>
        <p className="text-sm text-slate-600">
          {copy.doneBody}
        </p>
        <Link
          href={toLocalizedPath("/login", locale)}
          className="rounded-xl bg-brand-gradient py-2 text-center text-sm font-medium text-white shadow-pop"
        >
          {copy.login}
        </Link>
      </section>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex w-full max-w-sm flex-col gap-4 rounded-3xl bg-white/80 p-6 shadow-pop"
      noValidate
    >
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-xl text-slate-900">{copy.title}</h1>
        <p className="text-xs text-slate-500">
          {copy.intro}
        </p>
      </header>
      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.email}
        <input
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={pending}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        />
      </label>
      {error && (
        <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      )}
      <TurnstileWidget
        action="password-reset"
        onTokenChange={(token) => {
          turnstileTokenRef.current = token;
        }}
        resetSignal={turnstileResetSignal}
      />
      <button
        type="submit"
        disabled={pending}
        className="rounded-xl bg-brand-gradient py-2 text-sm font-medium text-white shadow-pop disabled:opacity-60"
      >
        {pending ? copy.pending : copy.submit}
      </button>
      <p className="text-center text-xs text-slate-400">
        {copy.back}{" "}
        <Link href={toLocalizedPath("/login", locale)} className="underline">
          {copy.backLabel}
        </Link>
      </p>
    </form>
  );
}
